import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { projectAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { VoteButtons } from '../components/VoteButtons';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { formatDistanceToNow } from 'date-fns';
import { Search, Plus, Github, ExternalLink, MessageSquare, Loader2, FolderOpen } from 'lucide-react';

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const sort = searchParams.get('sort') || 'recent';
  const tag = searchParams.get('tag') || '';
  const search = searchParams.get('search') || '';

  useEffect(() => {
    fetchProjects();
    fetchTags();
  }, [sort, tag, search]);

  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const response = await projectAPI.getAll({ sort, tag: tag || undefined, search: search || undefined });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await projectAPI.getTags();
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchValue = formData.get('search');
    const newParams = new URLSearchParams(searchParams);
    if (searchValue) {
      newParams.set('search', searchValue);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const handleSortChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('sort', value);
    setSearchParams(newParams);
  };

  const handleTagClick = (tagName) => {
    const newParams = new URLSearchParams(searchParams);
    if (tag === tagName) {
      newParams.delete('tag');
    } else {
      newParams.set('tag', tagName);
    }
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="projects-page">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Projects</h1>
            <p className="text-slate-600 mt-1">Discover hackathon projects from the community</p>
          </div>
          <Link to="/projects/create">
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Project
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="search"
                placeholder="Search projects..."
                defaultValue={search}
                className="pl-10 bg-white"
                data-testid="search-input"
              />
            </div>
          </form>
          
          <Select value={sort} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[180px] bg-white" data-testid="sort-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most Recent</SelectItem>
              <SelectItem value="popular">Most Popular</SelectItem>
              <SelectItem value="discussed">Most Discussed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {tags.map((t) => (
              <button
                key={t.tag}
                onClick={() => handleTagClick(t.tag)}
                className={`tag-chip ${tag === t.tag ? 'bg-indigo-100 text-indigo-700' : ''}`}
                data-testid={`tag-${t.tag}`}
              >
                {t.tag} ({t.count})
              </button>
            ))}
          </div>
        )}

        {/* Projects Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No projects found</h3>
            <p className="text-slate-600 mb-6">Be the first to share your hackathon project!</p>
            <Link to="/projects/create">
              <Button className="bg-indigo-600">Create Project</Button>
            </Link>
          </div>
        ) : (
          <div className="project-grid">
            {projects.map((project, index) => (
              <div
                key={project.project_id}
                className={`card-hover bg-white rounded-xl border border-slate-200 overflow-hidden animate-fade-in-up stagger-${(index % 5) + 1}`}
                data-testid={`project-card-${project.project_id}`}
              >
                {/* Image */}
                {project.images?.[0] && (
                  <div className="aspect-video bg-slate-100">
                    <img
                      src={project.images[0]}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="p-5">
                  {/* Author */}
                  <div className="flex items-center gap-2 mb-3">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={project.author_picture} />
                      <AvatarFallback className="text-xs bg-indigo-100 text-indigo-700">
                        {project.author_name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-slate-600">{project.author_name}</span>
                    <span className="text-xs text-slate-400">•</span>
                    <span className="text-xs text-slate-400">
                      {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <Link to={`/projects/${project.project_id}`}>
                    <h3 className="text-lg font-semibold text-slate-900 hover:text-indigo-600 transition-colors mb-2">
                      {project.title}
                    </h3>
                  </Link>
                  <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                    {project.description}
                  </p>

                  {/* Tags */}
                  {project.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {project.tags.slice(0, 3).map((t) => (
                        <Badge key={t} variant="secondary" className="text-xs">
                          {t}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-4">
                      <VoteButtons
                        targetType="project"
                        targetId={project.project_id}
                        initialUpvotes={project.upvotes}
                        initialDownvotes={project.downvotes}
                        userVote={project.user_vote}
                        size="sm"
                      />
                      <Link to={`/projects/${project.project_id}`} className="flex items-center gap-1 text-slate-500 hover:text-slate-700">
                        <MessageSquare className="w-4 h-4" />
                        <span className="text-sm">{project.comment_count}</span>
                      </Link>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {project.github_url && (
                        <a
                          href={project.github_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                          data-testid={`github-link-${project.project_id}`}
                        >
                          <Github className="w-4 h-4" />
                        </a>
                      )}
                      {project.demo_url && (
                        <a
                          href={project.demo_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          data-testid={`demo-link-${project.project_id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
